import React, { useEffect, useState, useRef } from 'react';
import { Mic, Square, Play, RefreshCcw } from 'lucide-react';
import AudioKnob from './AudioKnob';
import WaveformVisualizer from './WaveformVisualizer';
const VoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [knobValues, setKnobValues] = useState({
    tone: 50,
    depth: 50,
    clarity: 50,
    reverb: 30
  });
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);
  const handleKnobChange = (name: string, value: number) => {
    setKnobValues(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav'
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        if (audioRef.current) {
          audioRef.current.src = url;
        }
        setRecordingComplete(true);
        setRecordingTime(0);
        // Stop all tracks on the stream
        stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingComplete(false);
      // Start recording timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };
  const playRecording = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    }
  };
  const resetRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setRecordingComplete(false);
    setIsPlaying(false);
    audioChunksRef.current = [];
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="max-w-3xl mx-auto" data-id="element-9">
      <h1 className="text-3xl md:text-4xl font-bold text-center mb-8" data-id="element-10">
        Voice Recorder
      </h1>
      <div className="bg-gray-800 rounded-xl p-6 shadow-lg" data-id="element-11">
        <div className="flex flex-col items-center mb-8" data-id="element-12">
          <div className={`w-48 h-48 rounded-full flex items-center justify-center transition-all ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-700'}`} data-id="element-13">
            <button onClick={isRecording ? stopRecording : startRecording} className="w-40 h-40 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center hover:from-red-500 hover:to-red-600 transition-all shadow-lg" aria-label={isRecording ? 'Stop recording' : 'Start recording'} data-id="element-14">
              {isRecording ? <Square className="w-12 h-12" data-id="element-15" /> : <Mic className="w-12 h-12" data-id="element-16" />}
            </button>
          </div>
          <div className="mt-4 text-xl" data-id="element-17">
            {isRecording ? <div className="text-red-400 font-semibold" data-id="element-18">
                Recording... {formatTime(recordingTime)}
              </div> : recordingComplete ? <div className="text-green-400 font-semibold" data-id="element-19">
                Recording complete
              </div> : <div data-id="element-20">Tap to record</div>}
          </div>
        </div>
        <WaveformVisualizer isRecording={isRecording} data-id="element-21" />
        <div className="mt-6" data-id="element-22">
          <div className="flex justify-center space-x-4 mb-8" data-id="element-23">
            <button onClick={playRecording} disabled={!recordingComplete || isPlaying} className={`px-6 py-3 rounded-lg flex items-center ${!recordingComplete || isPlaying ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white'}`} data-id="element-24">
              <Play className="w-5 h-5 mr-2" data-id="element-25" />
              Play
            </button>
            <button onClick={resetRecording} disabled={!recordingComplete} className={`px-6 py-3 rounded-lg flex items-center ${!recordingComplete ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 hover:bg-gray-500 text-white'}`} data-id="element-26">
              <RefreshCcw className="w-5 h-5 mr-2" data-id="element-27" />
              Reset
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-28">
            <AudioKnob name="Tone" value={knobValues.tone} onChange={val => handleKnobChange('tone', val)} data-id="element-29" />
            <AudioKnob name="Depth" value={knobValues.depth} onChange={val => handleKnobChange('depth', val)} data-id="element-30" />
            <AudioKnob name="Clarity" value={knobValues.clarity} onChange={val => handleKnobChange('clarity', val)} data-id="element-31" />
            <AudioKnob name="Reverb" value={knobValues.reverb} onChange={val => handleKnobChange('reverb', val)} data-id="element-32" />
          </div>
        </div>
      </div>
      <audio ref={audioRef} className="hidden" data-id="element-33" />
    </div>;
};
export default VoiceRecorder;